/**
 * Core extension module.
 * This module creates the editor extension that wires everything together.
 *
 * Responsibilities:
 * - Create extension factory function
 * - Wire features into editor lifecycle hooks
 * - Register editor in registry
 * - Handle cleanup on editor destruction
 *
 * Architectural Rules:
 * - This module MAY import editor types (it's the extension entry point)
 * - It delegates to features and adapters for actual work
 */
import { Element, Operation } from 'slate';
import { getEditorId } from '@/adapters/host/doc';
import { setHistoryEditor } from '@/adapters/host/marks';
import { Constants } from '@/constants/common';
import { registerEditor } from '@/core/registry';
import { handleContentUpdate } from '@/features/updateContent';
/**
 * Checks if an element is a Velt comment element.
 */
const isVeltCommentText = (element) => {
    return Element.isElement(element) && element.type === Constants.ELEMENT_TYPE.VELT_COMMENT;
};
/**
 * Wraps the editor with Velt Comments functionality.
 *
 * This is the main entry point that enhances the editor with comment functionality.
 * It enhances the editor by:
 * - Overriding isInline and isVoid methods to handle comment elements
 * - Setting up onChange handler to detect and process document changes
 * - Registering the editor in the registry for state management
 *
 * @param editor - The Slate editor instance to enhance
 * @param config - Optional extension configuration including HistoryEditor and editorId
 * @returns Enhanced editor with Velt comments functionality
 *
 * @example
 * ```typescript
 * // Standard Slate plugin pattern
 * const editor = withVeltComments(withReact(createEditor()));
 *
 * // With configuration
 * const editor = withVeltComments(withReact(createEditor()), {
 *   HistoryEditor: myHistoryEditor,
 *   editorId: 'my-editor'
 * });
 * ```
 *
 * @remarks
 * - Follows standard Slate plugin pattern (like withReact, withHistory)
 * - Only processes text-related operations to avoid unnecessary updates
 * - Registers editor with default editorId if not provided
 */
export const withVeltComments = (editor, config) => {
    var _a;
    const { isInline, isVoid, onChange } = editor;
    // Set history editor if provided
    if (config === null || config === void 0 ? void 0 : config.HistoryEditor) {
        setHistoryEditor(config.HistoryEditor);
    }
    // 1. Get or create editorId
    // Try to get editorId from config first, then from DOM, fallback to 'default'
    // Note: getEditorId may return null if editor is not mounted yet, which is fine
    const editorId = (config === null || config === void 0 ? void 0 : config.editorId) || getEditorId(editor) || 'default';
    // 2. Register editor in registry
    registerEditor(editorId, editor, {
        persistVeltMarks: (_a = config === null || config === void 0 ? void 0 : config.persistVeltMarks) !== null && _a !== void 0 ? _a : true,
        editorId,
        HistoryEditor: config === null || config === void 0 ? void 0 : config.HistoryEditor,
    });
    // 3. Override editor methods
    // Override isInline to treat Velt comment text elements as inline elements
    // This ensures comment text flows naturally within the document
    editor.isInline = (element) => {
        return isVeltCommentText(element) ? true : isInline(element);
    };
    // Override isVoid to ensure Velt comment text elements are not void elements
    // This allows comment text to contain and display content
    editor.isVoid = (element) => {
        return isVeltCommentText(element) ? false : isVoid(element);
    };
    // 4. Set up onChange handler
    editor.onChange = () => {
        // Call the original onChange if it exists to preserve existing behavior
        if (onChange) {
            onChange();
        }
        // Only process text-related operations to avoid unnecessary updates
        const isTextChange = editor.operations.some((operation) => Operation.isTextOperation(operation));
        if (isTextChange) {
            // Update comment positions and states when text content changes
            handleContentUpdate(editorId, editor, undefined);
        }
    };
    // 5. Return enhanced editor
    return editor;
};
